import { useCallback, useMemo } from 'react';
import { getIn } from 'final-form';
import { v4 as uuidv4 } from 'uuid';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { IKnmErknmFormInspector } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormInspector';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { IKnmErknmFormAct } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormAct';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmPmFormAct } from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormAct';
import { IDecisionActForm } from 'DecisionPackage/MainModule/interfaces/IDecisionActForm';
import { Hint } from 'CorePackage/MainModule/components/Hint';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';

import styles from './KnmActSignInfo.module.scss';
import { ProfKindCodesEnum } from '../../../APIPackage/DictionariesModule/enums/ProfKindCodesEnum';
import { ExternalSystemSignInfoBlock } from '../../../CorePackage/MainModule/components/ExternalSystemSignInfoBlock';
import { Signers } from '../../SignersModule/components/Signers';
import { IDictionaryPositionSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { IDictionaryParticipantPositionSelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionaryParticipantPositionSelectOption';

interface IErknmActSignInfoProps {
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  id: string;
  inspectors: IKnmErknmFormInspector[] | undefined;
  isDecision?: boolean;
  isDisabledField: boolean;
  isErp: boolean;
  isKnm?: boolean;
  isPm?: boolean;
  isUseSigners: boolean;
  name: string;
  positionsSelectOptions: IDictionaryPositionSelectOption[] | IDictionaryParticipantPositionSelectOption[] | undefined;
  value: IKnmErknmFormAct | IKnmPmFormAct | IDecisionActForm;
}

export const KnmActSignInfo = ({
  formRef,
  id,
  inspectors,
  isDecision,
  isDisabledField,
  isErp,
  isKnm,
  isPm,
  isUseSigners,
  name,
  positionsSelectOptions,
  value,
}: IErknmActSignInfoProps) => {
  /**
   * Список вариантов для выбора должностного лица, подписавшего акт
   */
  const inspectorsErknmOptions = useMemo(() => {
    return (
      inspectors?.map(inspector => {
        return {
          id: inspector.guid || '',
          value: inspector,
          label: `${inspector.fullName} - ${inspector.position?.label}`,
        };
      }) || []
    );
  }, [inspectors]);

  /**
   * Функция изменения подписанта.
   * Если подписанта нет в knoInspectors внутри акта, то добавляем его туда
   */
  const handleFioAndPositionSingerChange = useCallback(
    (v: { id: string; value: IKnmErknmFormInspector; label: string } | undefined) => {
      if (v) {
        const listId = v.value?.listId || uuidv4();
        const newKnoInspector: IKnmErknmFormInspector = {
          knoInspector: v,
          ...v.value,
          listId,
          guid: v.value?.guid || listId,
        };

        if (value?.knoInspectors) {
          if (
            !value.knoInspectors.find(
              inspector =>
                inspector.knoInspector?.value.listId === v.value.listId ||
                inspector.listId === listId ||
                inspector.knoInspector?.value.fullName === v.value.fullName,
            )
          ) {
            // @ts-ignore
            formRef?.current?.change(`${name}.knoInspectors`, [...value.knoInspectors, newKnoInspector]);
          }
        } else {
          // @ts-ignore
          formRef?.current?.change(`${name}.knoInspectors`, [newKnoInspector]);
        }
      }
    },
    [formRef, name, value],
  );

  const hintText = useMemo(() => {
    if (isUseSigners) {
      if (isPm) {
        if (value?.firstExtractDateTime) {
          return (
            'Выбрать инспектора из раздела «Сведения о должностных лицах контрольных (надзорных) органов, ' +
            'участвующих в соответствующих профилактических мероприятиях» или добавить вручную'
          );
        }

        return (
          'Выбрать инспектора из раздела «Сведения о должностных лицах контрольных (надзорных) органов, ' +
          'участвующих в соответствующих профилактических мероприятиях»'
        );
      }

      if (isDecision) {
        if (value?.firstExtractDateTime) {
          return 'Выбрать инспектора из раздела «Сведения о должностных лицах, участвующих в КНМ, СР» или добавить вручную';
        }

        return 'Выбрать инспектора из раздела «Сведения о должностных лицах, участвующих в КНМ, СР»';
      }

      if (value?.firstExtractDateTime) {
        return 'Выбрать инспектора из раздела «Сведения о должностных лицах, участвующих в КНМ» или добавить вручную после подписания акта';
      }

      return 'Выбрать инспектора из раздела «Сведения о должностных лицах, участвующих в КНМ»';
    }

    if (isPm) {
      return (
        'Выбрать инспектора из раздела «Сведения о должностных лицах контрольных (надзорных) органов, ' +
        'участвующих в соответствующих профилактических мероприятиях»'
      );
    }

    if (isDecision) {
      return 'Выбрать инспектора из раздела «Сведения о должностных лицах, участвующих в КНМ, СР»';
    }

    return 'Выбрать инспектора из раздела «Сведения о должностных лицах, участвующих в КНМ»';
  }, [isUseSigners, isPm, isDecision, value?.isNew, value?.firstExtractDateTime]);

  // @ts-ignore
  // const formValue = formRef?.current.getFieldState(name)?.value;
  const formValue = getIn(formRef?.current.getState().values, name);
  // @ts-ignore
  const isPmPPType = isPm ? formRef?.current.getFieldState('pmType')?.value?.code === ProfKindCodesEnum.PP : false;

  let signInfoVariant;

  if (isUseSigners && !isErp) {
    signInfoVariant = (
      <Signers
        formRef={formRef}
        hasActOrResultDecisionFirstExtractDateTime={!!value?.firstExtractDateTime}
        hintText={hintText}
        inspectors={inspectors}
        isActOrResultDecisionSigned={value?.signed === true}
        isDecision={isDecision === true}
        isKnm={isKnm === true}
        isNewActOrResultDecision={value?.isNew === true}
        isPm={isPm === true}
        label="Подпись акта"
        parentId={id}
        parentName={name}
        positionsSelectOptions={positionsSelectOptions}
      />
    );
  } else if ((isDecision || (!isDecision && !isPm) || (isPm && isPmPPType)) && formValue?.systemSignatureUsed) {
    signInfoVariant = (
      <ExternalSystemSignInfoBlock
        systemSignatureDateTime={formValue.systemSignatureDateTime as string}
        systemSignatureTitle={formValue.systemSignatureTitle}
      />
    );
  } else {
    signInfoVariant = (
      <>
        <FieldBlock
          id={`${id}.fioAndPositionSinger`}
          label={getTitleFieldWithRedStar({
            label: `ФИО подписавшего ${isDecision ? 'решение' : 'акт'}`,
            field: `${name}.fioAndPositionSinger`,
            isPm,
            isDecision,
            isKnm: !isPm && !isDecision,
          })}
          color="black"
          placementLabel="left"
          className={styles.FieldBlock}
          margin="bottom"
        >
          <SelectInputField
            name={`${name}.fioAndPositionSinger`}
            placeholder="Выберите должностное лицо"
            options={inspectorsErknmOptions}
            disabled={isDisabledField}
            onChange={handleFioAndPositionSingerChange}
          />
        </FieldBlock>
        {hintText && <Hint text={hintText} />}
      </>
    );
  }

  return (
    <FieldBlock
      label={!isUseSigners && `Подпись ${isDecision ? 'решения' : 'акта'}`}
      className={styles.GroupMarginBottom}
    >
      <Row>{signInfoVariant}</Row>
    </FieldBlock>
  );
};
